import React, { useCallback, useContext, useEffect, useState } from "react"
import { Button, Card, Form, Modal } from "react-bootstrap"
import { v4 as uuid } from "uuid"

import { getFormattedMessage, isLTPM } from "../../../components/shared_components_registration"
import { FavouriteConfigContext } from "../../config/FavouriteConfigProvider"
import { GraphConfigContext } from "../../config/GraphConfigProvider"
import ReactSelectionElement from "../../widgets/ReactSelectionElement"
import { stringifyDate } from "../../utils"
import PageLoadingPanel from "../../widgets/PageLoadingPanel"

export const doCeilTwoDecimalRound = (value) => Number(Math.ceil(value + "e1") + "e-1")

export default function LiveReactorSelector({
  intl,
  showModal = false,
  showBtn = false,
  onBtnClick,
  btnDisabled = false,
  hideGraphSelector = false,
  defaultInfo,
  options,
  onSuccess,
}) {
  if (!isLTPM) return <></> // Code updates required for LanzaLake use
  const faveConfig = useContext(FavouriteConfigContext)
  const graphConfig = useContext(GraphConfigContext)
  const favouriteGraphSets = faveConfig?.favouriteGraphSets || []
  const [graphSets, setGraphSets] = useState([])
  const [selectedGraphSet, setSelectedGraphSet] = useState({})
  const { updateLiveGraphs, default_graph_configs = {} } = graphConfig
  const [defaultPageGraphs, setDefaultPageGraphs] = useState([])
  const [radioSelectionKey, setRadioSelectionKey] = useState(null)
  const [showGraphDetails, setShowGraphDetails] = useState(false)
  const toggleSelectedGraphs = (g) => {
    setSelectedGraphSet(g)
    setRadioSelectionKey(g.key)
  }
  useEffect(() => {
    if (default_graph_configs.length === 0) return
    const options = default_graph_configs.map((g) => ({ ...g, key: uuid() }))
    const match = options.find((g) => g.isDefault)
    setDefaultPageGraphs(options)
    toggleSelectedGraphs(match || options[0])
  }, [default_graph_configs])
  const [dropdownGraphSelection, setDropdownGraphSelection] = useState({})
  useEffect(() => {
    const faves = favouriteGraphSets.map((g) => ({ ...g, key: uuid() }))
    setGraphSets(faves)
    faves.length > 0 && setDropdownGraphSelection(faves[0])
  }, [favouriteGraphSets])

  const [show, setShow] = useState(showModal)
  const [runQuery, setRunQuery] = useState({
    cutoff: defaultInfo.cutoff,
    placeholder: defaultInfo.placeholder,
    sources: defaultInfo.sources,
  })
  useEffect(() => {
    setRunQuery({
      cutoff: defaultInfo.cutoff,
      placeholder: defaultInfo.placeholder,
      sources: defaultInfo.sources,
    })
  }, [defaultInfo])
  const updateRunQuery = useCallback(
    (field, val) =>
      setRunQuery((prev) => ({
        ...prev,
        [field]: val,
      })),
    [],
  )
  const current = runQuery.sources || []
  const onSubmit = (info) => {
    onSuccess(info)
    !hideGraphSelector &&
      updateLiveGraphs &&
      selectedGraphSet?.graphConfigs &&
      updateLiveGraphs(selectedGraphSet?.graphConfigs)
    setShow(false)
  }
  const [reactorGroups, setReactorGroups] = useState([])
  useEffect(() => {
    let groups = {}
    options.forEach((o) => {
      if (!(o.functional_group in groups)) {
        groups[o.functional_group] = []
      }
      groups[o.functional_group].push(o)
    })
    setReactorGroups(groups)
  }, [options])

  return (
    <>
      {show && (
        <Modal
          size={"xl"}
          dialogClassName={"common-modal"}
          show={show}
          onHide={() => onSubmit(defaultInfo)}
          style={{ overflowX: "scroll", overflowY: "scroll" }}
          backdrop={"static"}
        >
          <>
            <Modal.Header>
              <Modal.Title className={"text-center"}>
                {getFormattedMessage(
                  "fermmanagement.selective.run.loading",
                  "Selectively Load Runs",
                  true,
                  intl,
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className={"one-rem-margin-bottom"}>
                <div className={"row"}>
                  <div className={`col-3${!hideGraphSelector ? " solid-grey-border-r" : ""}`}>
                    <label>
                      {getFormattedMessage("graphing.timelabel.day", "Days", true, intl)}
                      &nbsp;
                      <span className={"fa fa-search"} style={{ fontSize: "85%" }} />:
                    </label>
                    <br />
                    <input
                      className={"form-control"}
                      type={"number"}
                      onWheel={(e) => e.target.blur()}
                      step={"0.1"}
                      value={runQuery.cutoff}
                      onChange={(e) => updateRunQuery("cutoff", e.target.value)}
                    />
                  </div>
                  <div key={"default-radio"} className={"col-9"}>
                    {!hideGraphSelector && (
                      <>
                        {selectedGraphSet.graphConfigs ? (
                          <div>
                            <label>
                              {getFormattedMessage("general.graph.set", "Graph Set", true, intl)}:
                            </label>
                            <hr style={{ margin: "10px 0px" }} />
                            {defaultPageGraphs.map((g, i) => {
                              return (
                                <div style={{ display: "flex", alignItems: "center" }} key={`default${i}`}>
                                  <Form.Check
                                    label={`${getFormattedMessage("general.default", "Default", true, intl)}: ${g?.name}`}
                                    name={"graph_select"}
                                    type={"radio"}
                                    checked={radioSelectionKey === g.key}
                                    onChange={() => toggleSelectedGraphs(g)}
                                    id={`graph-sel-radio-${i + 1}`}
                                  />
                                  &nbsp; (
                                  <div style={{ fontSize: "85%", fontStyle: "italic" }}>
                                    {g.graphConfigs.length}
                                    &nbsp;
                                    {getFormattedMessage("general.graphs", "Graphs", true, intl)}
                                  </div>
                                  )
                                </div>
                              )
                            })}
                            {graphSets.length > 0 && (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Form.Check
                                  label={`${getFormattedMessage(
                                    "favourites.favourite.graphs",
                                    "Favourite Graphs",
                                    true,
                                    intl,
                                  )}: `}
                                  name={"graph_select"}
                                  type={"radio"}
                                  checked={graphSets.map((g) => g.key).includes(radioSelectionKey)}
                                  onChange={() => toggleSelectedGraphs(dropdownGraphSelection)}
                                  id={`graph-sel-radio-${defaultPageGraphs.length + 1}`}
                                />
                                &nbsp;&nbsp;
                                <ReactSelectionElement
                                  name={"graphSetSelection"}
                                  list={graphSets.map((g) => ({ ...g, value: g }))}
                                  value={{ ...dropdownGraphSelection, value: dropdownGraphSelection }}
                                  onValueChange={(_, selection) => {
                                    setDropdownGraphSelection(selection)
                                    graphSets.map((g) => g.key).includes(radioSelectionKey) &&
                                      toggleSelectedGraphs(selection)
                                  }}
                                  required={false}
                                  isMulti={false}
                                  isClearable={false}
                                  klass={""}
                                  divStyle={{ width: "100%" }}
                                  formatOptionLabel={({ name, date_updated, graphConfigs }) => {
                                    return (
                                      <div>
                                        <div style={{ fontWeight: "bold" }}>{name}</div>
                                        <div style={{ fontSize: "85%", fontStyle: "italic" }}>
                                          {getFormattedMessage(
                                            "general.updated.on",
                                            "Updated On",
                                            true,
                                            intl,
                                          )}
                                          : &nbsp;
                                          {stringifyDate(date_updated)}
                                        </div>
                                        <div style={{ fontSize: "85%", fontStyle: "italic" }}>
                                          {graphConfigs.length}
                                          &nbsp;
                                          {getFormattedMessage("general.graphs", "Graphs", true, intl)}
                                        </div>
                                      </div>
                                    )
                                  }}
                                />
                              </div>
                            )}
                            <div>
                              <span
                                className={`fa fa-caret-${showGraphDetails ? "down" : "right"}`}
                                onClick={() => setShowGraphDetails(!showGraphDetails)}
                              />
                              &nbsp;
                              {selectedGraphSet.graphConfigs.length}
                              &nbsp;
                              {getFormattedMessage("general.graphs", "Graphs", true, intl)}
                              {showGraphDetails && (
                                <div
                                  className={"solid-grey-border"}
                                  style={{ padding: "10px", borderRadius: "5px" }}
                                >
                                  <ul style={{ margin: "0" }}>
                                    {selectedGraphSet.graphConfigs.map((g, i) => (
                                      <li key={`graph${i}`}>{g.title}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <PageLoadingPanel height={100} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <hr />
              <div>
                {Object.entries(reactorGroups).map(([group, groupOptions]) => {
                  return (
                    <div key={`${group}Reactors`} className={"one-rem-margin-bottom"}>
                      <label>{group}:</label>
                      <div className={"row"} style={{ margin: "0" }}>
                        {groupOptions.map((opt, idx) => {
                          const isChecked = current.includes(opt.source)
                          return (
                            <Card
                              key={`runOption${idx}`}
                              style={{ padding: "0" }}
                              className={"mb-1 col-lg-3 col-md-6 solid-grey-border"}
                            >
                              <Card.Body style={{ padding: "1rem" }}>
                                <div className={"row"}>
                                  <div className={"col-2 text-center"} style={{ padding: "0" }}>
                                    <Form.Check
                                      type={"checkbox"}
                                      checked={isChecked}
                                      onChange={(_) => {
                                        updateRunQuery(
                                          "sources",
                                          isChecked
                                            ? current.filter((s) => s !== opt.source)
                                            : [...current, opt.source],
                                        )
                                      }}
                                    />
                                  </div>
                                  <div className={"col-10"} style={{ padding: "0 5px" }}>
                                    <div>
                                      <b>
                                        {opt.placeholder && (
                                          <>
                                            <span
                                              title={getFormattedMessage(
                                                "graphing.run.placeholder",
                                                "Placeholder",
                                                true,
                                                intl,
                                              )}
                                              className="fa fa-bookmark-o text-success"
                                            />
                                            &nbsp;
                                          </>
                                        )}
                                        {opt.source}
                                      </b>
                                    </div>
                                    <div
                                      style={{
                                        fontStyle: "italic",
                                        fontSize: "85%",
                                        color: "var(--lanzalake-grey-mid)",
                                      }}
                                    >
                                      <div>
                                        <label>
                                          {getFormattedMessage(
                                            "graphing.run.identifier",
                                            "Run Identifier",
                                            true,
                                            intl,
                                          )}
                                          :
                                        </label>
                                        &nbsp;
                                        {opt.name}
                                      </div>
                                      <div>
                                        <label>Minimum Days Required:</label>{" "}
                                        {/* TODO - Consider translation */}
                                        &nbsp;
                                        {doCeilTwoDecimalRound(opt.cutoff_day)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                style={{ marginLeft: "auto" }}
                variant={"primary"}
                type={"submit"}
                disabled={
                  !runQuery.cutoff ||
                  (options.length > 0 && runQuery.sources.length === 0) ||
                  !selectedGraphSet?.graphConfigs
                }
                onClick={() => onSubmit(runQuery)}
              >
                {getFormattedMessage("general.submitButton", "Submit", true, intl)}
              </Button>
            </Modal.Footer>
          </>
        </Modal>
      )}
      {showBtn && (
        <Button
          size={"sm"}
          variant={"outline-primary"}
          onClick={(e) => {
            e.preventDefault()
            setShow(true)
            onBtnClick && onBtnClick()
          }}
          disabled={btnDisabled}
        >
          <span className={"fa fa-check-circle-o"} />
          &nbsp;
          {getFormattedMessage("general.reactors", "Reactors", true, intl)}
        </Button>
      )}
    </>
  )
}
